import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useCart } from "@/hooks/useCart";
import { useWishlist } from "@/hooks/useWishlist";
import SearchOverlay from "@/components/feature/SearchOverlay";

const navLinks = [
  { label: "i-clothes", href: "/collections/clothes" },
  { label: "i-shoes", href: "/collections/shoes" },
  { label: "i-bag", href: "/collections/bags" },
  { label: "i-nails", href: "/collections/nails" },
  { label: "New Arrivals", href: "/collections/new-arrivals" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const { itemCount, setIsOpen } = useCart();
  const { count: wishlistCount } = useWishlist();
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isTransparent = isHome && !scrolled;

  return (
    <>
      <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />

      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ${
          isTransparent
            ? "bg-transparent"
            : "bg-moonlight border-b border-pearl/40"
        }`}
        style={{ fontFamily: "'Jost', sans-serif" }}
      >
        <div className="px-6 lg:px-12 flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src="https://static.readdy.ai/image/11f299ce572862a7443fa4f5e7dec340/39eaa7af49292fe352c32005f65aa55b.png"
              alt="i-if logo"
              className="h-11 lg:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-xs tracking-widest uppercase transition-colors duration-300 whitespace-nowrap cursor-pointer ${
                  isTransparent
                    ? "text-moonlight/90 hover:text-white"
                    : "text-midnight hover:text-moon-gold-dark"
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Icons */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setSearchOpen(true)}
              className={`w-8 h-8 flex items-center justify-center cursor-pointer transition-colors duration-300 ${
                isTransparent
                  ? "text-moonlight/90 hover:text-white"
                  : "text-midnight hover:text-moon-gold-dark"
              }`}
              aria-label="Search"
            >
              <i className="ri-search-line text-lg" />
            </button>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              className={`w-8 h-8 flex items-center justify-center relative cursor-pointer transition-colors duration-300 ${
                isTransparent
                  ? "text-moonlight/90 hover:text-white"
                  : "text-midnight hover:text-moon-gold-dark"
              }`}
              aria-label="Wishlist"
            >
              <i className="ri-heart-line text-lg" />
              {wishlistCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-rose-400 text-white text-[10px] font-semibold flex items-center justify-center leading-none">
                  {wishlistCount > 9 ? "9+" : wishlistCount}
                </span>
              )}
            </Link>

            <button
              onClick={() => setIsOpen(true)}
              className={`w-8 h-8 flex items-center justify-center relative cursor-pointer transition-colors duration-300 ${
                isTransparent
                  ? "text-moonlight/90 hover:text-white"
                  : "text-midnight hover:text-moon-gold-dark"
              }`}
              aria-label="Shopping cart"
            >
              <i className="ri-shopping-bag-line text-lg" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-moon-gold-dark text-midnight text-[10px] font-semibold flex items-center justify-center leading-none">
                  {itemCount > 9 ? "9+" : itemCount}
                </span>
              )}
            </button>
            {/* Mobile Hamburger */}
            <button
              className={`w-8 h-8 flex items-center justify-center lg:hidden cursor-pointer transition-colors duration-300 ${
                isTransparent
                  ? "text-moonlight/90"
                  : "text-midnight"
              }`}
              onClick={() => setMobileOpen((v) => !v)}
              aria-label="Menu"
            >
              <i className={mobileOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-moonlight border-t border-pearl/40">
            <nav className="flex flex-col px-6 py-6 gap-5">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-xs tracking-widest uppercase text-midnight hover:text-moon-gold-dark transition-colors cursor-pointer"
                >
                  {link.label}
                </Link>
              ))}
              <div className="pt-2 border-t border-pearl/40 flex gap-6">
                <button
                  onClick={() => { setMobileOpen(false); setSearchOpen(true); }}
                  className="text-xs tracking-wider uppercase text-midnight/60 hover:text-midnight transition-colors cursor-pointer whitespace-nowrap"
                >
                  Search
                </button>
                <Link to="/pages/size-guide" className="text-xs tracking-wider uppercase text-midnight/60 hover:text-midnight transition-colors cursor-pointer">
                  Size Guide
                </Link>
                <Link to="/pages/about" className="text-xs tracking-wider uppercase text-midnight/60 hover:text-midnight transition-colors cursor-pointer">
                  About i-if
                </Link>
              </div>
            </nav>
          </div>
        )}
      </header>
    </>
  );
}
